import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { ShieldCheck, Eye, EyeOff, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [form, setForm] = useState({ newPass: '', confirm: '' });
  const [show, setShow] = useState({ new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (form.newPass.length < 8) { toast.error('Mínimo 8 caracteres'); return; }
    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        token,
        new_password: form.newPass,
        confirm_password: form.confirm,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al restablecer contraseña');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-soem-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 bg-soem-primary rounded-sm flex items-center justify-center">
            <ShieldCheck size={22} className="text-white" />
          </div>
          <span className="text-soem-navy font-black text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>SOEM</span>
        </div>

        <div className="soem-card p-8 animate-fade-in">
          {!token ? (
            <div className="text-center">
              <AlertCircle size={40} className="text-red-500 mx-auto mb-3" />
              <h2 className="font-bold text-soem-navy mb-2">Enlace inválido</h2>
              <p className="text-slate-500 text-sm mb-4">El enlace de restablecimiento no es válido o ha expirado.</p>
              <Link to="/forgot-password" className="text-soem-primary text-sm font-semibold hover:underline">
                Solicitar nuevo enlace
              </Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-soem-navy mb-2">¡Contraseña actualizada!</h2>
              <p className="text-slate-500 text-sm">Serás redirigido al inicio de sesión en unos segundos...</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-soem-navy mb-2">Nueva contraseña</h2>
              <p className="text-slate-500 text-sm mb-6">Ingresa y confirma tu nueva contraseña.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  { key: 'newPass', label: 'Nueva contraseña', showKey: 'new' },
                  { key: 'confirm', label: 'Confirmar contraseña', showKey: 'confirm' },
                ].map(({ key, label, showKey }) => (
                  <div key={key}>
                    <label htmlFor={`f-pwd-${key}`} className="soem-label block mb-1.5">{label}</label>
                    <div className="relative">
                      <input id={`f-pwd-${key}`} type={show[showKey] ? 'text' : 'password'}
                        value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        placeholder="Mínimo 8 caracteres" required
                        className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                        data-testid={`reset-${key}`} />
                      <button type="button"
                        onClick={() => setShow((s) => ({ ...s, [showKey]: !s[showKey] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {show[showKey] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                ))}
                {form.newPass && form.confirm && form.newPass !== form.confirm && (
                  <p className="text-red-600 text-sm flex items-center gap-1"><AlertCircle size={13} /> Las contraseñas no coinciden</p>
                )}
                <button type="submit" disabled={loading}
                  className="w-full soem-btn-primary py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="reset-submit-btn">
                  {loading ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Actualizando...</> : <><KeyRound size={14} /> Restablecer contraseña</>}
                </button>
              </form>
              <div className="mt-4 text-center">
                <Link to="/login" className="text-slate-400 text-sm hover:text-soem-primary">Volver al inicio de sesión</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
